import React from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Container,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";
import { AlertTriangle, Check, RefreshCw, Server } from "react-feather";
import { useGet, usePost } from "../../api";
import { IApiQuarantineResponse } from "../../interfaces";

/**
 * Merchants whose platform API path has been quarantined.
 *
 * A merchant lands here only after the self-repair loop has genuinely run out
 * of road: its credentials failed three runs in a row, a capture replaced them,
 * and that happened twice over without a single successful run in between. At
 * that point the cause is far more likely to be the platform's own contract
 * changing than anything about the merchant — which is why this page groups by
 * platform and error rather than listing merchants.
 *
 * Nothing here is lost work: a quarantined merchant keeps being tested on a
 * browser path. This page is about finding the one handler change behind forty
 * merchants failing the same way.
 */
export const ApiQuarantine: React.FC = () => {
  const { data, loading, error, refetch } =
    useGet<IApiQuarantineResponse>("/merchants/api-quarantine");
  const clearPost = usePost<{ cleared: number }, { merchantIds?: string[] }>();
  const [busy, setBusy] = React.useState<string | null>(null);
  const [cleared, setCleared] = React.useState<Record<string, number>>({});

  const release = async (key: string, merchantIds?: string[]) => {
    setBusy(key);
    try {
      const res = await clearPost.execute(
        "/merchants/api-quarantine/clear",
        merchantIds ? { merchantIds } : {}
      );
      setCleared((prev) => ({ ...prev, [key]: res?.cleared ?? 0 }));
      await refetch();
    } catch {
      window.alert("Could not release those merchants.");
    } finally {
      setBusy(null);
    }
  };

  const groups = data?.groups ?? [];

  return (
    <Container fluid className="py-4">
      <Row className="mb-3">
        <Col>
          <h2 className="mb-0">
            <Server className="me-2" />
            API quarantine
          </h2>
          <small className="text-muted">
            Merchants whose platform API path failed, was re-credentialed, and
            failed again. They still run on a browser path — this is the queue
            for finding the handler change behind them.
          </small>
        </Col>
        <Col xs="auto" className="d-flex align-items-start gap-2">
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => refetch()}
            disabled={loading}
          >
            <RefreshCw size={14} className="me-1" /> Refresh
          </Button>
          {groups.length > 0 && (
            <Button
              variant="outline-primary"
              size="sm"
              disabled={!!busy}
              onClick={() => {
                if (
                  window.confirm(
                    `Release all ${data?.total ?? 0} quarantined merchant(s)? Do this after a handler fix — each will try the API path again on its next run.`
                  )
                ) {
                  release("__all__");
                }
              }}
            >
              {busy === "__all__" ? (
                <Spinner size="sm" animation="border" />
              ) : (
                <>Release all</>
              )}
            </Button>
          )}
        </Col>
      </Row>

      {error && (
        <Alert variant="danger">Could not load the quarantine queue.</Alert>
      )}

      {loading && !data && (
        <div className="text-center py-5">
          <Spinner animation="border" />
        </div>
      )}

      {data && groups.length === 0 && (
        <Alert variant="success">
          <Check size={16} className="me-2" />
          No merchant's API path is quarantined. Every platform merchant with
          stored credentials is using them.
        </Alert>
      )}

      {data && groups.length > 0 && (
        <Alert variant="warning">
          <AlertTriangle size={16} className="me-2" />
          <strong>{data.total}</strong>{" "}
          {data.total === 1 ? "merchant" : "merchants"} across{" "}
          <strong>{groups.length}</strong>{" "}
          {groups.length === 1 ? "distinct fault" : "distinct faults"}. Each
          group below is most likely one bug in a platform handler, not one bug
          per merchant.
        </Alert>
      )}

      {groups.map((g) => {
        const key = `${g.checkoutType}|${g.reason}`;
        const done = cleared[key];
        return (
          <Card className="mb-3" key={key}>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <div>
                <Badge bg="dark" className="me-2 text-uppercase">
                  {g.checkoutType}
                </Badge>
                <span className="fw-bold">{g.reason}</span>
              </div>
              <div className="d-flex align-items-center gap-2">
                <Badge bg="danger">
                  {g.merchants}{" "}
                  {g.merchants === 1 ? "merchant" : "merchants"}
                </Badge>
                {done !== undefined ? (
                  <Badge bg="success">
                    <Check size={12} className="me-1" />
                    released {done}
                  </Badge>
                ) : (
                  <Button
                    size="sm"
                    variant="outline-primary"
                    disabled={!!busy}
                    onClick={() =>
                      release(
                        key,
                        g.examples.map((e) => e.id)
                      )
                    }
                    title={
                      g.merchants > g.examples.length
                        ? `Releases the ${g.examples.length} shown. Use "Release all" once the handler is fixed.`
                        : "Release these merchants so they try the API path again"
                    }
                  >
                    {busy === key ? (
                      <Spinner size="sm" animation="border" />
                    ) : (
                      <>Release shown</>
                    )}
                  </Button>
                )}
              </div>
            </Card.Header>
            <Card.Body className="p-0">
              <Table size="sm" className="mb-0">
                <thead>
                  <tr>
                    <th>Merchant</th>
                    <th style={{ width: 180 }}>Quarantined</th>
                  </tr>
                </thead>
                <tbody>
                  {g.examples.map((e) => (
                    <tr key={e.id}>
                      <td>
                        <a href={`/merchants/${e.id}`}>{e.domain || e.id}</a>
                      </td>
                      <td className="text-muted small">
                        {e.since
                          ? new Date(e.since).toLocaleString()
                          : "unknown"}
                      </td>
                    </tr>
                  ))}
                  {g.merchants > g.examples.length && (
                    <tr>
                      <td colSpan={2} className="text-muted small fst-italic">
                        …and {g.merchants - g.examples.length} more with the
                        same fault
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        );
      })}
    </Container>
  );
};
